'use client';

import { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import { useAuth } from '@/contexts/AuthContext';
import Icon from '@/components/ui/AppIcon';
import Link from 'next/link';
import { createClient } from '@/lib/supabase/client';

const LoginPage = () => {
  const router = useRouter();
  const { signIn, user, role, loading: authLoading } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // If the visitor is already signed in, don't show the login form —
  // route them to the dashboard that matches their role.
  useEffect(() => {
    if (authLoading || !user) return;
    if (role === 'admin') {
      router.replace('/admin');
    } else if (role === 'manager') {
      router.replace('/manager-dashboard');
    } else {
      router.replace('/customer-dashboard');
    }
  }, [authLoading, user, role, router]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Step 1: Sign in
      console.log('[Login][handleSubmit] ▶ START — attempting signIn for:', email);
      const { user } = await signIn(email, password);

      if (!user) {
        console.error('[Login][handleSubmit] ✖ signIn returned no user');
        setError('Sign in failed. Please try again.');
        setLoading(false);
        return;
      }

      console.log('[Login][handleSubmit] ✔ signIn succeeded — email:', user.email, '| userId:', user.id);

      // Step 2: Fetch role directly — no reliance on AuthContext state timing
      const supabase = createClient();
      let resolvedRole: string | null = null;

      console.log('[Login][handleSubmit] ▶ Querying user_profiles for userId:', user.id);
      const { data: profile, error: profileError } = await supabase
        .from('user_profiles')
        .select('role, onboarding_completed')
        .eq('id', user.id)
        .maybeSingle();

      if (profileError) {
        console.error('[Login][handleSubmit] ✖ Profile fetch error:', profileError.message, '| code:', profileError.code);
      }

      console.log('[Login][handleSubmit] ✔ Raw profile row from DB:', JSON.stringify(profile));

      if (profile) {
        resolvedRole = profile.role ?? null;
        console.log('[Login][handleSubmit] ✔ Role from user_profiles:', resolvedRole, '| onboarding_completed:', profile.onboarding_completed);
      } else {
        // No profile — create one via API (new customer registration)
        console.warn('[Login][handleSubmit] ⚠ No profile found for userId:', user.id, '— creating via API');
        const res = await fetch('/api/auth/create-profile', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            userId: user.id,
            email: user.email,
            fullName: user.user_metadata?.full_name || user.email?.split('@')[0] || 'User',
            phone: user.user_metadata?.phone || null,
          }),
        });

        const createResult = await res.json();
        console.log('[Login][handleSubmit] ✔ create-profile API response (status', res.status, '):', JSON.stringify(createResult));

        if (res.ok) {
          resolvedRole = createResult.role ?? 'customer';
        } else {
          console.error('[Login][handleSubmit] ✖ create-profile API failed:', createResult);
          resolvedRole = 'customer';
        }
        console.log('[Login][handleSubmit] ✔ Profile created with role:', resolvedRole);
      }

      // Step 3: Route based on role
      console.log('[Login][handleSubmit] ▶ ROUTING DECISION — resolvedRole:', resolvedRole, '| email:', user.email);

      if (resolvedRole === 'admin') {
        console.log('[Login][handleSubmit] ✔ Admin detected → navigating to /admin');
        router.push('/admin');
      } else if (resolvedRole === 'manager') {
        // Check onboarding
        const onboardingDone = profile?.onboarding_completed !== false;
        console.log('[Login][handleSubmit] ✔ Manager detected | onboarding_completed:', profile?.onboarding_completed, '| onboardingDone:', onboardingDone);
        if (!onboardingDone) {
          console.log('[Login][handleSubmit] ✔ Manager → navigating to /manager-onboarding');
          router.push('/manager-onboarding');
        } else {
          console.log('[Login][handleSubmit] ✔ Manager → navigating to /manager-dashboard');
          router.push('/manager-dashboard');
        }
      } else {
        console.log('[Login][handleSubmit] ✔ Customer/unknown role ("' + resolvedRole + '") → navigating to /customer-dashboard');
        router.push('/customer-dashboard');
      }
    } catch (err: any) {
      console.error('[Login][handleSubmit] ✖ Exception during sign in:', err);
      setError(err.message || 'Failed to sign in');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4 sm:px-6 py-8">
      <div className="max-w-md w-full">
        {/* Header */}
        <div className="text-center mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-4xl font-bold text-foreground mb-2">
            Welcome to <span className="text-primary">Containers by Take a Zip</span>
          </h1>
          <p className="text-sm sm:text-base text-muted-foreground">Sign in to access your account</p>
        </div>

        {/* Login Form */}
        <div className="container-card p-6 sm:p-8">
          {/* Loading State Announcement */}
          {loading && (
            <div className="sr-only" aria-live="assertive" aria-atomic="true">
              Signing in, please wait...
            </div>
          )}
          
          <form onSubmit={handleSubmit} className="space-y-5 sm:space-y-6">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm" role="alert" aria-live="assertive">
                {error}
              </div>
            )}

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-foreground mb-2">
                Email Address
              </label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full border border-gray-300 rounded-xl px-4 py-3 sm:py-4 text-base focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all touch-manipulation min-h-[48px]"
                placeholder="your@email.com"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-foreground mb-2">
                Password
              </label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full border border-gray-300 rounded-xl px-4 py-3 sm:py-4 text-base focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all touch-manipulation min-h-[48px]"
                placeholder="••••••••"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground font-semibold rounded-full px-8 py-4 sm:py-5 hover:bg-primary/90 transition-all disabled:opacity-50 disabled:cursor-not-allowed text-base sm:text-lg touch-manipulation min-h-[56px]"
            >
              {loading ? (
                <>
                  <Icon name="ArrowPathIcon" size={20} className="animate-spin" />
                  Signing in...
                </>
              ) : (
                <>
                  Sign In
                  <Icon name="ArrowRightIcon" size={20} />
                </>
              )}
            </button>
          </form>

          <div className="mt-6 text-center space-y-2">
            <p className="text-sm text-muted-foreground">
              Don&apos;t have an account?{' '}
              <Link href="/register" className="text-primary hover:text-primary/80 font-semibold">
                Register Now
              </Link>
            </p>
            <Link href="/homepage" className="text-sm text-primary hover:text-primary/80">
              Back to Homepage
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;